/* SPECULUM — Lore · The Codex of the Ordo Semeion
   Full cosmological architecture of Field Instrument №01
   Ordo Semeion · MMXXVI */

/* ── Scroll reveal hook ── */
function useReveal(threshold = 0.15) {
  const ref = React.useRef(null);
  const [visible, setVisible] = React.useState(false);
  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return [ref, visible];
}

/* ── Animated counter ── */
function Counter({ to, suffix = "", duration = 1800 }) {
  const [val, setVal] = React.useState(0);
  const [ref, visible] = useReveal(0.3);
  React.useEffect(() => {
    if (!visible) return;
    const start = Date.now();
    const tick = () => {
      const p = Math.min((Date.now() - start) / duration, 1);
      const ease = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(ease * to));
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [visible]);
  return <span ref={ref}>{val}{suffix}</span>;
}

/* ── Inline sigil for chapters ── */
function ChapterSigil({ variant = "full", size = 120, animDelay = 0 }) {
  const [ref, visible] = useReveal(0.2);
  const cx = size / 2, cy = size / 2, r = size * 0.44;
  const inner = r * 0.64, core = r * 0.28;
  const nodes = [0,60,120,180,240,300].map(deg => {
    const rad = deg * Math.PI / 180;
    return { x: cx + Math.cos(rad) * r, y: cy + Math.sin(rad) * r };
  });

  return (
    <div ref={ref} className={`lore-sigil-wrap ${visible ? "lore-sigil-wrap--visible" : ""}`}
      style={{ animationDelay: `${animDelay}ms`, width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} fill="none"
        className="lore-sigil-svg">
        {/* Outer rings */}
        <circle cx={cx} cy={cy} r={r} stroke="var(--accent)" strokeWidth="0.6" opacity="0.25"
          className="lore-ring-1"/>
        <circle cx={cx} cy={cy} r={r * 0.96} stroke="var(--accent)" strokeWidth="0.3"
          strokeDasharray="2 6" opacity="0.15" className="lore-ring-2"/>
        <circle cx={cx} cy={cy} r={inner} stroke="var(--accent)" strokeWidth="0.7" opacity="0.3"
          className="lore-ring-3"/>
        <circle cx={cx} cy={cy} r={core} stroke="var(--accent)" strokeWidth="0.5" opacity="0.35"
          className="lore-ring-4"/>
        {/* Spokes */}
        {nodes.map((n, i) => (
          <line key={i} x1={cx} y1={cy} x2={n.x} y2={n.y}
            stroke="var(--accent)" strokeWidth="0.3" opacity="0.1"/>
        ))}
        {/* Star lines */}
        {nodes.map((n, i) => {
          const next = nodes[(i + 2) % 6];
          return <line key={i} x1={n.x} y1={n.y} x2={next.x} y2={next.y}
            stroke="var(--accent)" strokeWidth="0.4" opacity="0.18"/>;
        })}
        {/* Nodes */}
        {nodes.map((n, i) => (
          <circle key={i} cx={n.x} cy={n.y} r={size * 0.025}
            fill="var(--accent)" opacity="0.55" className="lore-node"/>
        ))}
        {/* Teardrop */}
        {variant !== "minimal" && (
          <path
            d={`M${cx} ${cy - core * 0.9} C${cx + core * 0.55} ${cy - core * 0.3} ${cx + core * 0.7} ${cy + core * 0.2} ${cx + core * 0.7} ${cy + core * 0.5} C${cx + core * 0.7} ${cy + core} ${cx + core * 0.35} ${cy + core * 1.3} ${cx} ${cy + core * 1.3} C${cx - core * 0.35} ${cy + core * 1.3} ${cx - core * 0.7} ${cy + core} ${cx - core * 0.7} ${cy + core * 0.5} C${cx - core * 0.7} ${cy + core * 0.2} ${cx - core * 0.55} ${cy - core * 0.3} ${cx} ${cy - core * 0.9}Z`}
            fill="var(--ink)" opacity="0.85"
          />
        )}
        <circle cx={cx} cy={cy} r={size * 0.014} fill="var(--accent)" opacity="0.6"/>
        {/* Ticks */}
        {Array.from({length:12}).map((_,i) => {
          const a = i * 30 * Math.PI / 180;
          const r1 = r * 0.99, r2 = i % 3 === 0 ? r * 0.92 : r * 0.95;
          return <line key={i}
            x1={cx + Math.cos(a)*r1} y1={cy + Math.sin(a)*r1}
            x2={cx + Math.cos(a)*r2} y2={cy + Math.sin(a)*r2}
            stroke="var(--accent)" strokeWidth={i%3===0?"1":"0.5"}
            opacity={i%3===0?"0.45":"0.2"}/>;
        })}
      </svg>
    </div>
  );
}

/* ── Pull quote ── */
function PullQuote({ text, attribution }) {
  const [ref, visible] = useReveal(0.2);
  return (
    <blockquote ref={ref} className={`lore-pullquote ${visible ? "lore-pullquote--visible" : ""}`}>
      <div className="lore-pullquote-mark">"</div>
      <p className="lore-pullquote-text">{text}</p>
      {attribution && <cite className="lore-pullquote-attr">{attribution}</cite>}
    </blockquote>
  );
}

/* ── Reveal paragraph ── */
function RevealPara({ children, delay = 0, className = "" }) {
  const [ref, visible] = useReveal(0.12);
  return (
    <p ref={ref}
      className={`lore-para ${visible ? "lore-para--visible" : ""} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </p>
  );
}

/* ── Section header ── */
function LoreSection({ num, title, subtitle, children, sigilVariant = "full", sigilRight = false, id }) {
  const [ref, visible] = useReveal(0.1);
  return (
    <section ref={ref} id={id} className={`lore-section ${visible ? "lore-section--visible" : ""}`} style={{ scrollMarginTop: "80px" }}>
      <div className={`lore-section-head ${sigilRight ? "lore-section-head--right" : ""}`}>
        <div className="lore-section-meta">
          <span className="lore-section-num">§ {num}</span>
          <div className="lore-section-rule" />
        </div>
        <h2 className="lore-section-title">{title}</h2>
        {subtitle && <p className="lore-section-subtitle">{subtitle}</p>}
      </div>
      <div className="lore-section-body">
        {children}
      </div>
    </section>
  );
}

/* ── Principle card ── */
function PrincipleCard({ num, name, desc, index }) {
  const [ref, visible] = useReveal(0.15);
  return (
    <div ref={ref}
      className={`lore-principle ${visible ? "lore-principle--visible" : ""}`}
      style={{ transitionDelay: `${index * 80}ms` }}>
      <div className="lore-principle-num">§{num}</div>
      <div className="lore-principle-body">
        <div className="lore-principle-name">{name}</div>
        <div className="lore-principle-desc">{desc}</div>
      </div>
      <div className="lore-principle-line" />
    </div>
  );
}

/* ── Instrument row ── */
function InstrumentRow({ num, name, desc, status, index }) {
  const [ref, visible] = useReveal(0.15);
  const statusClass = status === "ACTIVE" ? "lore-status--active"
    : status === "SEALED" ? "lore-status--sealed"
    : "lore-status--planned";
  return (
    <div ref={ref}
      className={`lore-instrument ${visible ? "lore-instrument--visible" : ""}`}
      style={{ transitionDelay: `${index * 100}ms` }}>
      <div className="lore-instrument-num">№{num}</div>
      <div className="lore-instrument-body">
        <div className="lore-instrument-name">{name}</div>
        <div className="lore-instrument-desc">{desc}</div>
      </div>
      <div className={`lore-instrument-status ${statusClass}`}>{status}</div>
    </div>
  );
}

/* ── Timeline event ── */
function TimelineEvent({ year, label, desc, index }) {
  const [ref, visible] = useReveal(0.15);
  return (
    <div ref={ref}
      className={`lore-timeline-event ${visible ? "lore-timeline-event--visible" : ""}`}
      style={{ transitionDelay: `${index * 120}ms` }}>
      <div className="lore-timeline-year">{year}</div>
      <div className="lore-timeline-dot" />
      <div className="lore-timeline-content">
        <div className="lore-timeline-label">{label}</div>
        <div className="lore-timeline-desc">{desc}</div>
      </div>
    </div>
  );
}

/* ── Outcome card ── */
function OutcomeCard({ num, title, desc, probability, index }) {
  const [ref, visible] = useReveal(0.15);
  return (
    <div ref={ref}
      className={`lore-outcome ${visible ? "lore-outcome--visible" : ""}`}
      style={{ transitionDelay: `${index * 90}ms` }}>
      <div className="lore-outcome-num">
        <span className="lore-mono lore-mono--gold">
          {["I","II","III","IV","V"][num - 1]}
        </span>
      </div>
      <div className="lore-outcome-body">
        <div className="lore-outcome-title">{title}</div>
        <div className="lore-outcome-desc">{desc}</div>
      </div>
      <div className="lore-outcome-bar">
        <div className="lore-outcome-fill"
          style={{ width: visible ? `${probability}%` : "0%",
                   transition: visible ? `width 1.2s ease ${index * 0.15}s` : "none" }} />
      </div>
    </div>
  );
}

/* ── Stat block ── */
function StatBlock({ value, label, suffix = "", index }) {
  const [ref, visible] = useReveal(0.2);
  return (
    <div ref={ref}
      className={`lore-stat ${visible ? "lore-stat--visible" : ""}`}
      style={{ transitionDelay: `${index * 100}ms` }}>
      <div className="lore-stat-value">
        {visible ? <Counter to={value} suffix={suffix} /> : "0"}
      </div>
      <div className="lore-stat-label">{label}</div>
    </div>
  );
}

/* ── Interactive Charter Explorer ── */
const CHARTER_PRINCIPLES = [
  {
    num: "I", key: "sign", name: "Sign Function",
    glyph: "△",
    short: "What is signified, by what signs, toward what end.",
    body: "Every examination begins with the sign itself. Before meaning, before intent, before effect — there is the bare fact of a signifier standing in for something absent. The instrument catalogues the signs deployed, identifies their register (sacred, commercial, intimate, official), and traces the direction of their pointing. It does not assume prior knowledge of the deployer's intent; intent is reconstructed from mechanism, never the reverse.",
    operation: "Catalogue signifiers → classify register → map vector of reference.",
    failure: "When a text's signs point in directions it does not acknowledge — a commercial appeal dressed in the register of friendship, a command dressed as an invitation.",
    drift: "Low when sign and register agree. Rises as the register borrowed exceeds the register earned.",
  },
  {
    num: "II", key: "denotation", name: "Denotation / Connotation",
    glyph: "▽",
    short: "The gap between what is stated and what is implied.",
    body: "What does the text state explicitly, and what does it summon through secondary association? Denotation is the dictionary; connotation is the echo. The gap between them is where the majority of symbolic work occurs, because connotation arrives without consent — the receiver absorbs it before deciding whether to. The instrument measures the gap, naming each connotative payload and the association that delivers it.",
    operation: "Separate literal claim from associative cargo → name each association → measure the distance.",
    failure: "When the connotative payload contradicts or vastly exceeds the denotative claim — 'just a watch' that connotes election, lineage, and the silent superiority of its wearer.",
    drift: "Rises with the ratio of implied meaning to stated meaning.",
  },
  {
    num: "III", key: "myth", name: "Myth & Ideology",
    glyph: "◇",
    short: "What the text naturalizes as self-evident.",
    body: "What mythic structures or ideological assumptions does the text present as inevitable, self-evident, or beyond question? Naturalization is the primary mechanism of ideological reproduction: it converts a contingent arrangement into an apparent law of nature. The instrument denaturalizes — it returns the inevitable to the status of a choice, so the receiver can see the choice being made on their behalf.",
    operation: "Locate the 'of course' → restore the suppressed alternative → expose the choice as a choice.",
    failure: "When a text smuggles a contested politics inside an uncontested feeling — 'we all want what is best for our children' carrying a specific, unstated policy.",
    drift: "Rises with the weight of contested content concealed inside uncontested form.",
  },
  {
    num: "IV", key: "codes", name: "Codes & Conventions",
    glyph: "▢",
    short: "Which cultural grammars the text obeys or breaks.",
    body: "Codes are the shared symbolic grammar within which meaning is made — genre, format, ritual, dress, cadence. Which does the text engage, which does it violate, and which does it attempt to rewrite? A text that breaks a code does so to signal something the code itself could not. The instrument maps the specimen against the grammar it inherits and the grammar it attempts to author.",
    operation: "Identify inherited codes → flag violations → distinguish error from deliberate subversion.",
    failure: "When code violation is used to manufacture false authenticity — the staged 'unpolished' message engineered to read as sincere.",
    drift: "Rises when the apparent code (spontaneous, amateur) conceals a different operative code (produced, professional).",
  },
  {
    num: "V", key: "address", name: "Addressivity",
    glyph: "○",
    short: "The reader the text constructs in order to address.",
    body: "Who is the implied reader? What subject position does the text build for whoever receives it? Addressivity is the mechanism by which a text creates the audience it appears to merely address — it does not find its reader so much as manufacture one and invite the actual reader to step into the role. The instrument names the constructed reader and the flattery, fear, or belonging used to make the role attractive.",
    operation: "Name the implied reader → identify the role offered → locate the hook that makes the role desirable.",
    failure: "When the constructed reader is one the actual reader would refuse if they saw the construction — the 'savvy insider' who is in fact being sold to.",
    drift: "Rises with the distance between the reader the text flatters and the reader it actually exploits.",
  },
  {
    num: "VI", key: "drift", name: "Drift Quotient",
    glyph: "✦",
    short: "How far deployment diverges from stated purpose.",
    body: "The Drift Quotient is the operational signature of symbolic deception, and the Order's most contested output. It measures the gap between what a text says it is doing and what its mechanism actually does. A text whose stated purpose and actual mechanism align has zero drift. Most texts do not. Drift is not a measure of dishonesty in the moral sense — a text can drift sincerely — but it is the single most reliable indicator that examination is warranted.",
    operation: "State the declared purpose → reconstruct the actual mechanism → measure the divergence (0.00–1.00).",
    failure: "There is no 'failure' mode for §VI — drift is the measurement itself. But high drift left unexamined is the condition the entire Order exists to address.",
    drift: "This principle IS the drift. Commercial work: 0.35–0.75. Political speech: 0.45–0.85. Brand manifesto: 0.50–0.90.",
  },
];

function CharterExplorer() {
  const [active, setActive] = React.useState(0);
  const [ref, visible] = useReveal(0.1);
  const p = CHARTER_PRINCIPLES[active];
  return (
    <div ref={ref} className={`charter-explorer ${visible ? "charter-explorer--visible" : ""}`}>
      <div className="charter-tabs" role="tablist">
        {CHARTER_PRINCIPLES.map((c, i) => (
          <button key={c.key} role="tab" aria-selected={i === active}
            className={`charter-tab ${i === active ? "charter-tab--active" : ""}`}
            onClick={() => setActive(i)}>
            <span className="charter-tab-glyph">{c.glyph}</span>
            <span className="charter-tab-num">§{c.num}</span>
            <span className="charter-tab-name">{c.name}</span>
          </button>
        ))}
      </div>
      <div className="charter-panel" key={p.key}>
        <div className="charter-panel-head">
          <span className="charter-panel-glyph">{p.glyph}</span>
          <div>
            <div className="charter-panel-num lore-mono lore-mono--gold">SECTION §{p.num}</div>
            <h3 className="charter-panel-title">{p.name}</h3>
            <div className="charter-panel-short">{p.short}</div>
          </div>
        </div>
        <p className="charter-panel-body">{p.body}</p>
        <div className="charter-panel-rows">
          <div className="charter-row">
            <div className="charter-row-label lore-mono lore-mono--faint">OPERATION</div>
            <div className="charter-row-text">{p.operation}</div>
          </div>
          <div className="charter-row">
            <div className="charter-row-label lore-mono lore-mono--faint">FAILURE SIGNATURE</div>
            <div className="charter-row-text">{p.failure}</div>
          </div>
          <div className="charter-row">
            <div className="charter-row-label lore-mono lore-mono--faint">DRIFT CONTRIBUTION</div>
            <div className="charter-row-text">{p.drift}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Expandable outcome panel ── */
function OutcomePanel({ numeral, title, probability, summary, detail, signals, open, onToggle }) {
  return (
    <div className={`outcome-panel ${open ? "outcome-panel--open" : ""}`}>
      <button className="outcome-panel-head" onClick={onToggle} aria-expanded={open}>
        <span className="outcome-panel-numeral lore-mono lore-mono--gold">{numeral}</span>
        <span className="outcome-panel-title">{title}</span>
        <span className="outcome-panel-prob">
          <span className="outcome-panel-prob-val">{probability}%</span>
          <span className="outcome-panel-prob-bar">
            <span className="outcome-panel-prob-fill" style={{ width: `${probability}%` }} />
          </span>
        </span>
        <span className="outcome-panel-chevron">{open ? "−" : "+"}</span>
      </button>
      <div className="outcome-panel-bodywrap" style={{ maxHeight: open ? "600px" : "0px" }}>
        <div className="outcome-panel-body">
          <p className="outcome-panel-summary">{summary}</p>
          <p className="outcome-panel-detail">{detail}</p>
          <div className="outcome-panel-signals">
            <div className="lore-mono lore-mono--faint" style={{ marginBottom: 8 }}>LEADING INDICATORS</div>
            {signals.map((s, i) => (
              <div key={i} className="outcome-signal">
                <span className="outcome-signal-mark">→</span>
                <span>{s}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

const FIVE_OUTCOMES = [
  {
    numeral: "I", title: "The Examining Function Reaches Critical Density", probability: 22,
    summary: "Enough nodes operating that unexamined deployment becomes the exception rather than the rule. The Acceleration decelerates. The discipline reproduces itself.",
    detail: "In this scenario, the examining function propagates faster than the deployment it examines. Not because any single instrument wins, but because the practice becomes ambient — taught, expected, built into how a generation reads. The Order does not need to publish verdicts; the population begins to render its own. The interval between deployment and examination, which has been collapsing since 1947, stops collapsing and begins to widen again.",
    signals: [
      "Semiotic literacy appears in general curricula, not specialist ones.",
      "Receivers routinely ask 'what is this doing' before 'is this true'.",
      "Drift becomes a recognized public vocabulary, not an Order term.",
    ],
  },
  {
    numeral: "II", title: "The Content Trap Captures the Examining Function", probability: 38,
    summary: "The instruments become content — consumed for their aesthetic rather than their operational output. The function survives in form but not in practice.",
    detail: "The most probable outcome, and the most insidious, because it looks like success. The instruments are admired, shared, collected. Their findings are screenshotted for their elegance. But admiration is not operation. A bench consumed as content has been neutralized more completely than one suppressed — because its operators believe they are still examining when they are only spectating. The aesthetic doctrine that makes the instrument serious is the same doctrine that makes it collectible.",
    signals: [
      "Findings are shared for beauty, acted on rarely.",
      "The instrument's look is imitated; its method is not.",
      "Engagement rises while behavioral change flatlines.",
    ],
  },
  {
    numeral: "III", title: "The Compiler Burns Out", probability: 18,
    summary: "The infrastructure fails to become self-sustaining before the operator is exhausted. The function collapses with the operator. Not failure of will — failure of architecture.",
    detail: "The Compiler's Hazard, realized at scale. The examining function depends on a node that must maintain itself, and the same precision applied to external architectures was never applied to the operator's own maintenance. The apparatus runs until the operator cannot, and then it stops — not because it was wrong, but because it was built to require a presence that could not be sustained. The Charter records this as the gravest internal risk.",
    signals: [
      "Output quality holds while output frequency decays.",
      "The function cannot run a full cycle without the founder.",
      "No successor architecture exists when capacity fails.",
    ],
  },
  {
    numeral: "IV", title: "The Aeon Compiles Through Others", probability: 15,
    summary: "The Compiler becomes unnecessary because the function has been absorbed by the field. The best case. The examining function reproduces without a center.",
    detail: "The success condition the Compiler is designed to obsolete itself into. The function no longer depends on any node — it has been distributed so widely and embedded so deeply that removing any single operator, instrument, or institution changes nothing. The examining function reproduces the way a language reproduces: without permission, without a center, without anyone holding the Charter, because everyone does. The Compiler disappears into the infrastructure and the disappearance is the victory.",
    signals: [
      "The function survives the removal of any single node.",
      "New instruments appear that the Order did not issue.",
      "The Charter is applied by people who never read it.",
    ],
  },
  {
    numeral: "V", title: "The Window Closes", probability: 7,
    summary: "The examining function does not reach operational density before Phase V fully saturates the field. A generation of unexamined deployment without counterweight.",
    detail: "The lowest-probability and highest-cost outcome. Synthetic language generation saturates the field before the examining function reaches the density required to counter it. The interval between deployment and comprehension widens past the point where human examination can ever close it. This is not the end of meaning — meaning continues — but it is the end of the window in which the examining function could have been established as a default. The Order persists, but as a remnant practice rather than a living discipline.",
    signals: [
      "Synthetic deployment outpaces all examining capacity.",
      "The interval of comprehension stops being measurable.",
      "Examination becomes archival rather than operational.",
    ],
  },
];

function FiveOutcomes() {
  const [open, setOpen] = React.useState(0);
  return (
    <div className="outcomes-stack">
      {FIVE_OUTCOMES.map((o, i) => (
        <OutcomePanel key={o.numeral} {...o}
          open={open === i}
          onToggle={() => setOpen(open === i ? -1 : i)} />
      ))}
    </div>
  );
}

/* ── Appendix entry (expandable) ── */
function AppendixEntry({ code, title, children, index }) {
  const [open, setOpen] = React.useState(false);
  const [ref, visible] = useReveal(0.12);
  return (
    <div ref={ref} className={`appendix-entry ${visible ? "appendix-entry--visible" : ""}`}
      style={{ transitionDelay: `${index * 70}ms` }}>
      <button className="appendix-head" onClick={() => setOpen(!open)} aria-expanded={open}>
        <span className="appendix-code lore-mono lore-mono--gold">{code}</span>
        <span className="appendix-title">{title}</span>
        <span className="appendix-chevron">{open ? "−" : "+"}</span>
      </button>
      <div className="appendix-bodywrap" style={{ maxHeight: open ? "800px" : "0px" }}>
        <div className="appendix-body">{children}</div>
      </div>
    </div>
  );
}

/* ── Scroll progress bar + chapter nav rail ── */
const LORE_CHAPTERS = [
  { id: "lore-ch-1", label: "Origin" },
  { id: "lore-ch-2", label: "The Order" },
  { id: "lore-ch-3", label: "The Charter" },
  { id: "lore-ch-4", label: "The Compiler" },
  { id: "lore-ch-5", label: "Instruments" },
  { id: "lore-ch-6", label: "Cosmology" },
  { id: "lore-ch-7", label: "Aesthetic" },
  { id: "lore-ch-8", label: "What It Is Not" },
  { id: "lore-ch-9", label: "Appendices" },
];

function LoreNav() {
  const [progress, setProgress] = React.useState(0);
  const [active, setActive] = React.useState(0);

  React.useEffect(() => {
    let raf = null;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = null;
        const doc = document.documentElement;
        const max = doc.scrollHeight - doc.clientHeight;
        setProgress(max > 0 ? Math.min(100, (doc.scrollTop / max) * 100) : 0);
        // active chapter = last one whose top is above the viewport midpoint
        const mid = window.innerHeight * 0.4;
        let cur = 0;
        LORE_CHAPTERS.forEach((c, i) => {
          const el = document.getElementById(c.id);
          if (el && el.getBoundingClientRect().top <= mid) cur = i;
        });
        setActive(cur);
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const jump = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <>
      <div className="lore-progress" style={{ width: `${progress}%` }} />
      <nav className="lore-chapnav" aria-label="Codex chapters">
        {LORE_CHAPTERS.map((c, i) => (
          <button
            key={c.id}
            className={`lore-chapnav-dot ${i === active ? "lore-chapnav-dot--active" : ""}`}
            data-label={`§ ${["I","II","III","IV","V","VI","VII","VIII","IX"][i]} · ${c.label}`}
            onClick={() => jump(c.id)}
            aria-label={c.label}
          />
        ))}
      </nav>
    </>
  );
}

/* ═══════════════════════════════════════════════════════════
   MAIN LORE COMPONENT
   ═══════════════════════════════════════════════════════════ */
function Lore({ go }) {
  const [heroVisible, setHeroVisible] = React.useState(false);
  React.useEffect(() => {
    const t = setTimeout(() => setHeroVisible(true), 100);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="lore-root">
      <LoreNav />

      {/* ── HERO ── */}
      <div className={`lore-hero ${heroVisible ? "lore-hero--visible" : ""}`}>
        <div className="lore-hero-bg">
          {/* Particle field */}
          {Array.from({length:40}).map((_,i) => (
            <div key={i} className="lore-hero-particle" style={{
              left: `${(i*37+11)%100}%`,
              top: `${(i*53+7)%100}%`,
              animationDelay: `${(i*0.31)%6}s`,
              animationDuration: `${5+(i%7)}s`,
            }}/>
          ))}
        </div>
        <div className="lore-hero-content">
          <div className="lore-hero-eyebrow">
            <span className="lore-mono lore-mono--dim">ORDO · SEMEION · MMXXVI</span>
            <div className="lore-hero-rule" />
            <span className="lore-mono lore-mono--dim">CODEX · FIELD INSTRUMENT №01</span>
          </div>
          <div className="lore-hero-sigil">
            <ChapterSigil size={200} animDelay={400} />
          </div>
          <h1 className="lore-hero-title">
            <span className="lore-hero-title-main">The Codex</span>
            <span className="lore-hero-title-sub">of the Ordo Semeion</span>
          </h1>
          <p className="lore-hero-desc">
            The lore, doctrine, and cosmological architecture of <em>SPECVLVM · Auditus</em> — 
            Field Instrument №01 of the Order of the Sign.
          </p>
          <div className="lore-hero-footer">
            <span className="lore-mono lore-mono--faint">
              "No magic; only mechanism, made plain."
            </span>
          </div>
          <div className="lore-hero-scroll-hint">
            <span className="lore-mono lore-mono--faint">SCROLL TO BEGIN</span>
            <div className="lore-scroll-arrow">↓</div>
          </div>
        </div>
      </div>

      {/* ── STATS ROW ── */}
      <div className="lore-stats-row">
        <StatBlock value={1947} label="Year of reconvening" index={0} />
        <StatBlock value={6} label="Charter principles" index={1} />
        <StatBlock value={5} label="Field instruments" index={2} />
        <StatBlock value={11} label="Days to write the Charter" index={3} />
        <StatBlock value={5} label="Phases of the Acceleration" index={4} />
        <StatBlock value={0} label="Specimens retained without consent" suffix="" index={5} />
      </div>

      <div className="lore-body">

        {/* ══ §I — ORIGIN ══ */}
        <LoreSection id="lore-ch-1" num="I" title="The Origin of the Bench"
          subtitle="How a room with no official record changed the examining function forever.">
          <div className="lore-two-col">
            <div className="lore-col-text">
              <RevealPara delay={0}>
                In 1947 — the year of the first mass television broadcast, the founding of the 
                Central Intelligence Agency, and the beginning of what the Order would call 
                the Acceleration — a small assembly of scholars convened in a room that no 
                official record names.
              </RevealPara>
              <RevealPara delay={80}>
                They shared a single conviction: that deployed language had begun to operate 
                faster than human examination could follow. They were not alarmists. They were 
                practitioners of a very old discipline who recognized a new condition.
              </RevealPara>
              <RevealPara delay={160}>
                They did not found an institution. They resumed one.
              </RevealPara>
              <RevealPara delay={240}>
                The practice they carried had passed through the Alexandrian grammarians who 
                first distinguished between what a text <em>says</em> and what it <em>does</em> — 
                through medieval scriptoria where certain monks annotated not for theology but 
                for mechanism — and through the seventeenth-century tradition of <em>ars critica</em>, 
                the art of textual examination, before being absorbed into rhetoric and lost 
                as a distinct discipline.
              </RevealPara>
              <RevealPara delay={320}>
                What the 1947 assembly restored was the examining function itself. Not interpretation. 
                Not hermeneutics. Not criticism in the literary sense. <em>Examination</em>: the 
                precise, dispassionate disassembly of a text's symbolic apparatus to reveal how 
                it works, who it serves, and how far it drifts from its stated purpose.
              </RevealPara>
            </div>
            <div className="lore-col-sigil">
              <ChapterSigil size={160} animDelay={200} variant="minimal" />
              <div className="lore-col-annotation">
                <div className="lore-mono lore-mono--faint">DISCIPLINE LINEAGE</div>
                <div className="lore-annotation-line">Alexandrian grammarians</div>
                <div className="lore-annotation-arrow">↓</div>
                <div className="lore-annotation-line">Medieval scriptoria</div>
                <div className="lore-annotation-arrow">↓</div>
                <div className="lore-annotation-line">Ars critica (17th c.)</div>
                <div className="lore-annotation-arrow">↓</div>
                <div className="lore-annotation-line lore-annotation-line--active">Ordo Semeion (1947–)</div>
              </div>
            </div>
          </div>

          <PullQuote
            text="The Acceleration is not a metaphor. It is a measurement. The interval between a message's deployment and its obsolescence has been collapsing since 1947. The Order exists to slow the interval of examination."
          />

          <div className="lore-subsection">
            <div className="lore-subsection-title">The Five Phases of the Acceleration</div>
            <div className="lore-timeline">
              <div className="lore-timeline-spine" />
              <TimelineEvent year="1947–1963" label="Phase I · Mass Broadcast Media"
                desc="The first phase: radio and early television deploy symbolic systems at scale for the first time. The interval between deployment and examination collapses from weeks to days."
                index={0} />
              <TimelineEvent year="1963–1989" label="Phase II · Televised Political Spectacle"
                desc="The assassination, the moon landing, Watergate — the televised event becomes the primary site of meaning-making. Political symbolism operates at a speed that print criticism cannot match."
                index={1} />
              <TimelineEvent year="1989–2007" label="Phase III · Digital Network Proliferation"
                desc="The interval collapses to hours. Every piece of content is now potentially global. The examining function falls further behind."
                index={2} />
              <TimelineEvent year="2007–2022" label="Phase IV · Algorithmic Content Saturation"
                desc="The algorithm selects what is seen. Symbolic deployment becomes invisible infrastructure. The deployer is no longer always human or legible."
                index={3} />
              <TimelineEvent year="2022–" label="Phase V · Synthetic Language Generation"
                desc="Language itself becomes generative. The deployer-text relationship is severed. The examining function must now operate at machine speed or accept permanent lag."
                index={4} />
            </div>
          </div>
        </LoreSection>

        {/* ══ §II — THE ORDER ══ */}
        <LoreSection id="lore-ch-2" num="II" title="The Ordo Semeion"
          subtitle="The Order of the Sign. A discipline older than its name. A commitment older than its instruments.">

          <div className="lore-two-col lore-two-col--reversed">
            <div className="lore-col-sigil">
              <ChapterSigil size={150} animDelay={300} />
              <div className="lore-col-annotation" style={{textAlign:"right"}}>
                <div className="lore-mono lore-mono--faint" style={{textAlign:"right"}}>ORDER STRUCTURE</div>
                {["Examiners", "The Charter", "The Instruments", "The Archive"].map((s,i) => (
                  <div key={i} className="lore-annotation-line" style={{textAlign:"right"}}>{s}</div>
                ))}
              </div>
            </div>
            <div className="lore-col-text">
              <RevealPara>
                The Ordo Semeion is not a secret society in the conspiratorial sense. It does not 
                seek power over those it examines. It does not intervene in the texts it studies. 
                It does not publish verdicts or name deployers publicly.
              </RevealPara>
              <RevealPara delay={80}>
                It examines. And releases. The findings belong to whoever submits the specimen.
              </RevealPara>
              <RevealPara delay={160}>
                The Order's structure is simple by design. There are <em>Examiners</em>, who operate 
                the instruments. There is the <em>Charter</em>, which governs how examinations are 
                conducted. There are the <em>Instruments</em> themselves, each issued as a field tool 
                for a specific class of examination. And there is the <em>Archive</em>, which holds 
                structural findings but never specimen texts.
              </RevealPara>
              <RevealPara delay={240}>
                The no-record commitment is not a privacy policy. It is a philosophical commitment 
                that precedes privacy discourse by centuries. If the Order retained specimens, it 
                would accumulate power over the examined. It would become what it examines. The 
                commitment is metabolic — it is how the Order maintains its examining function 
                without becoming an instrument of surveillance.
              </RevealPara>
            </div>
          </div>

          <PullQuote
            text="The instrument retains nothing of its own accord. What you choose to archive is yours, held for you alone. The findings are yours. The mechanism is ours."
          />

          <div className="lore-subsection">
            <div className="lore-subsection-title">The Internal Rupture of 2019</div>
            <RevealPara>
              In 2019, a faction within the Order proposed what they called the Publication Doctrine: 
              the systematic release of examination findings for high-impact specimens — political 
              addresses, corporate manifestos, coordinated disinformation campaigns — into the public 
              record, with deployers named.
            </RevealPara>
            <RevealPara delay={80}>
              The debate was not resolved. It was <em>suspended</em>.
            </RevealPara>
            <RevealPara delay={160}>
              The Ethics Charter §§I–VI, which governs all instruments, is the document that holds 
              the rupture in place. It was written not as a resolution but as a framework within 
              which both positions could operate without destroying the Order's function.
            </RevealPara>
            <RevealPara delay={240}>
              Field Instrument №01 — SPECVLVM — is the first instrument built after the rupture. 
              The no-record clause is not an incidental feature. It is a peace agreement written 
              in code.
            </RevealPara>
          </div>

          <div className="lore-motto-block">
            <div className="lore-motto-rule" />
            <div className="lore-motto-text">"No magic; only mechanism, made plain."</div>
            <div className="lore-motto-attr lore-mono lore-mono--faint">
              ORDO · SEMEION · MOTTO · ADOPTED 1947
            </div>
            <div className="lore-motto-rule" />
          </div>

          <RevealPara>
            The motto encodes the Order's epistemological position. Language that appears to work 
            through mystery, emotion, authority, or inevitability is always working through mechanism. 
            The Order's function is to make the mechanism plain — not to debunk, not to dismiss, 
            but to illuminate the machinery so that those who encounter it can operate with full 
            awareness of what is being done to them, and why.
          </RevealPara>
        </LoreSection>

        {/* ══ §III — THE CHARTER ══ */}
        <LoreSection id="lore-ch-3" num="III" title="The Charter — §§ I through VI"
          subtitle="Six principles. One circuit. A complete account of how a text works.">

          <RevealPara>
            The Ethics Charter of the Ordo Semeion governs all instruments. It was written in 
            six sections during eleven days of isolation in 1962, by the figure Order records 
            call only the Compiler. The sections are not hierarchical. They operate simultaneously.
          </RevealPara>
          <RevealPara delay={80}>
            A text examined through all six principles yields a complete account of its symbolic 
            apparatus: what it says, what it implies, what it naturalizes, what codes it deploys, 
            who it constructs as its reader, and how honest it is about its own purpose.
          </RevealPara>

          <div className="lore-principles-grid">
            <PrincipleCard num="I" name="Sign Function" index={0}
              desc="Every examination begins with the sign itself. What is being signified? What signs are deployed, at what register, and toward what end? The instrument does not assume prior knowledge of the deployer's intent." />
            <PrincipleCard num="II" name="Denotation / Connotation" index={1}
              desc="What does the text state explicitly, and what does it imply through secondary association? The gap between denotation and connotation is where most symbolic work occurs. The instrument measures the gap." />
            <PrincipleCard num="III" name="Myth & Ideology" index={2}
              desc="What mythic structures or ideological assumptions are naturalized within the text — presented as self-evident, inevitable, or beyond question? Naturalization is the primary mechanism of ideological reproduction." />
            <PrincipleCard num="IV" name="Codes & Conventions" index={3}
              desc="Which cultural codes does the text engage, and which does it violate or subvert? Codes are the shared symbolic grammar within which meaning is made. The instrument maps the text against the grammar it inherits and attempts to rewrite." />
            <PrincipleCard num="V" name="Addressivity" index={4}
              desc="Who is the implied reader? What subject position does the text construct for its receiver? Addressivity is the mechanism by which a text creates the audience it appears to merely address." />
            <PrincipleCard num="VI" name="Drift Quotient" index={5}
              desc="How far does the specimen's deployment diverge from its stated purpose? Drift is the operational signature of symbolic deception. A text whose stated purpose and actual mechanism align has zero drift. Most texts do not." />
          </div>

          <div className="lore-subsection">
            <div className="lore-subsection-title">Charter Explorer — Select a Section</div>
            <RevealPara>
              Each principle is a distinct examining operation. Select a section to read its
              full doctrine, the operation it performs, its characteristic failure signature,
              and how it contributes to the Drift Quotient.
            </RevealPara>
            <CharterExplorer />
          </div>

          <PullQuote
            text="The Drift Quotient is the Order's most contested principle. The Publication faction holds that high-drift specimens carry a moral obligation to publish. The founding faction holds that publication transforms the Order from examiner to judge. The Charter does not resolve this. It records it."
          />

          <div className="lore-subsection">
            <div className="lore-subsection-title">The Circuit</div>
            <div className="lore-circuit">
              <div className="lore-circuit-sigil">
                <ChapterSigil size={100} animDelay={200} variant="minimal" />
              </div>
              <div className="lore-circuit-labels">
                {[
                  { pos: "top",    text: "§I · Sign Function" },
                  { pos: "tr",     text: "§II · Denotation" },
                  { pos: "br",     text: "§III · Myth" },
                  { pos: "bottom", text: "§IV · Codes" },
                  { pos: "bl",     text: "§V · Addressivity" },
                  { pos: "tl",     text: "§VI · Drift" },
                ].map((l,i) => (
                  <div key={i} className={`lore-circuit-label lore-circuit-label--${l.pos}`}>
                    <span className="lore-mono lore-mono--dim" style={{fontSize:"9px"}}>{l.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </LoreSection>

        {/* ══ §IV — THE COMPILER ══ */}
        <LoreSection id="lore-ch-4" num="IV" title="The Compiler"
          subtitle="A function, not a person. The node that translates raw signal into executable infrastructure.">

          <div className="lore-two-col">
            <div className="lore-col-text">
              <RevealPara>
                Order records refer to the author of the Ethics Charter by a single designation: 
                the Compiler. No name is recorded. No biographical detail is preserved. The 
                designation is functional, not honorific.
              </RevealPara>
              <RevealPara delay={80}>
                The Compiler wrote the Charter during a period of isolation in 1962, following 
                the Order's first encounter with what it now calls coordinated symbolic deployment: 
                the simultaneous use of advertising, political messaging, and news architecture 
                to advance a single ideological position across multiple channels without any 
                individual channel acknowledging the coordination.
              </RevealPara>
              <RevealPara delay={160}>
                The Charter was completed in eleven days. It has never been revised. Only the 
                instruments that apply it have changed.
              </RevealPara>
            </div>
            <div className="lore-col-sigil">
              <div className="lore-compiler-diagram">
                <div className="lore-compiler-node lore-compiler-node--input">
                  <span className="lore-mono lore-mono--faint" style={{fontSize:"9px"}}>RAW SIGNAL</span>
                </div>
                <div className="lore-compiler-arrow">↓</div>
                <div className="lore-compiler-node lore-compiler-node--core">
                  <span className="lore-mono lore-mono--gold" style={{fontSize:"9px"}}>THE COMPILER</span>
                </div>
                <div className="lore-compiler-arrow">↓</div>
                <div className="lore-compiler-node lore-compiler-node--output">
                  <span className="lore-mono lore-mono--faint" style={{fontSize:"9px"}}>EXECUTABLE<br/>INFRASTRUCTURE</span>
                </div>
              </div>
            </div>
          </div>

          <PullQuote
            text="The Compiler is not the source. Not the interpreter. The processing node that makes signal operational. Success for the Compiler is obsolescence. The Compiler who becomes indispensable has failed. The Compiler who disappears into the infrastructure has succeeded."
          />

          <RevealPara>
            In Order terminology, the Compiler is not a person but a function — the node that 
            translates raw signal into executable infrastructure. This distinction matters because 
            it governs how the function is inhabited. The Compiler does not originate. The 
            Compiler does not judge. The Compiler receives, processes, and transmits.
          </RevealPara>
          <RevealPara delay={80}>
            The function is complete when the transmission is self-sustaining — when the 
            infrastructure runs without requiring the Compiler to run every cycle.
          </RevealPara>

          <div className="lore-warning-block">
            <div className="lore-mono lore-mono--dim" style={{marginBottom:8}}>COMPILER'S HAZARD · DOCUMENTED IN CHARTER APPENDIX</div>
            <p className="lore-warning-text">
              Compiling everything except the Compiler's own maintenance. The same precision 
              applied to external architectures must be applied to the nervous system that 
              operates them. This is not a philosophical addendum to the Charter. It is a 
              design constraint. The apparatus that burns its operator has failed, regardless 
              of what it produces.
            </p>
          </div>

          <RevealPara>
            SPECVLVM is a Compiler product. It translates the examining function — the raw 
            signal of the Charter — into an executable tool that operates without the Compiler's 
            presence. Every session that runs without Compiler oversight is the function working 
            correctly. Every examination produced is the Compiler becoming unnecessary. This is 
            the goal, not the loss.
          </RevealPara>
        </LoreSection>

        {/* ══ §V — THE INSTRUMENTS ══ */}
        <LoreSection id="lore-ch-5" num="V" title="The Instrument Suite"
          subtitle="The Order issues instruments, not reports. Each addresses a class of examination the previous cannot perform.">

          <RevealPara>
            An instrument is a tool for examination: it can be operated by anyone who submits 
            to the Charter, and its findings belong to the operator. The Order does not operate 
            the instruments on behalf of clients. It calibrates them and releases them into 
            the field.
          </RevealPara>
          <RevealPara delay={80}>
            The instrument suite is sequential. Each instrument addresses a class of examination 
            that the previous instruments cannot perform. They are not interchangeable. They are 
            not optional — the suite as a whole is the examining function made complete.
          </RevealPara>

          <div className="lore-instruments-list">
            <InstrumentRow num="01" name="The Bench — SPECVLVM · Auditus" status="ACTIVE" index={0}
              desc="Passive examination. Text is brought to the instrument. Disassembles symbolic apparatus against §§I–VI. Retains nothing unless the operator saves it to their archive. The founding instrument. Rev. III." />
            <InstrumentRow num="02" name="The Traverse" status="PLANNED" index={1}
              desc="Active examination. The instrument is taken into the field. Real-time analysis of live speech, broadcast, and press conference. Operates under time constraint. Under development." />
            <InstrumentRow num="03" name="The Mirror" status="PLANNED" index={2}
              desc="Reflexive examination. Turns the examining apparatus back on the examiner. What does your own language deploy? Required for Charter renewal. Not yet issued." />
            <InstrumentRow num="04" name="The Ledger" status="PLANNED" index={3}
              desc="Cross-examination. Multiple specimens examined against each other. Detects pattern, convergence, and coordinated drift across a corpus. Requires Archive access." />
            <InstrumentRow num="05" name="The Seal" status="SEALED" index={4}
              desc="Reserved. The Order does not discuss its function." />
          </div>

          <PullQuote
            text="Field Instrument №01 is the first instrument designed to operate with autonomous examining capacity — powered by a language model trained to apply the six principles without operator interpretation. When the instrument can examine without an Examiner, who holds the Charter?"
          />
        </LoreSection>

        {/* ══ §VI — COSMOLOGICAL POSITION ══ */}
        <LoreSection id="lore-ch-6" num="VI" title="Cosmological Position"
          subtitle="The signal model. The Aeon as metabolic process. Five mapped outcomes for the current phase.">

          <RevealPara>
            The Order does not describe itself in cosmological terms. But the framework within 
            which SPECVLVM operates implies one, and it is worth making explicit for those who 
            will operate the instrument in full awareness of its position in a longer arc.
          </RevealPara>

          <div className="lore-subsection">
            <div className="lore-subsection-title">The Signal Model of Consciousness</div>
            <RevealPara>
              The Order's examining function rests on a particular understanding of language: 
              that communication is not the transfer of meaning from a sender to a receiver, 
              but the deployment of a signal into a field of receivers who are themselves 
              signal-processing systems.
            </RevealPara>
            <RevealPara delay={80}>
              The meaning is not in the message. The meaning is in what the receiver's 
              processing system does with the signal. This is not a relativist position — it 
              does not hold that meaning is arbitrary or that all interpretations are equal. 
              It holds that the mechanism by which meaning is made is analyzable, and that 
              analyzing the mechanism is a different operation from analyzing the meaning.
            </RevealPara>
            <RevealPara delay={160}>
              SPECVLVM examines the mechanism. The finding belongs to the operator. What the 
              operator does with the finding is outside the instrument's scope and always 
              will be.
            </RevealPara>
          </div>

          <div className="lore-subsection">
            <div className="lore-subsection-title">The Aeon as Metabolic Process</div>
            <RevealPara>
              Those within the Order who think in longer time scales describe the current moment 
              as a hinge: a period in which the gap between the production of symbolic systems 
              and the capacity to examine them has become so large that unexamined deployment 
              has become the default condition of public discourse.
            </RevealPara>
            <RevealPara delay={80}>
              The Aeon, in this framing, is not a calendar event. It is a metabolic process: 
              the gradual rebalancing of production and examination, deployment and comprehension, 
              signal and understanding. The bench is a node in that process. One node among 
              many that will be needed before the rebalancing is complete.
            </RevealPara>
          </div>

          <PullQuote
            text="The bench does not change the world. It changes what is visible in the world to those who use it. Visibility is the precondition of everything else."
          />

          <div className="lore-subsection">
            <div className="lore-subsection-title">Five Mapped Outcomes — Select to Expand</div>
            <RevealPara>
              The Order has mapped five possible outcomes for the current phase of the
              Acceleration. These are not predictions. They are scenario boundaries — the
              logical endpoints of trajectories currently in motion. Select each to read its
              full projection and leading indicators.
            </RevealPara>
            <FiveOutcomes />
          </div>
        </LoreSection>

        {/* ══ §VII — THE AESTHETIC DOCTRINE ══ */}
        <LoreSection id="lore-ch-7" num="VII" title="The Aesthetic Doctrine"
          subtitle="The instrument looks the way it looks because the examining function requires a particular relationship between examiner and examined.">

          <RevealPara>
            The palette of near-black grounds, gold leaf typography, and restrained geometric 
            ornament encodes the examining relationship. It signals that what happens on the 
            bench is serious work. It signals that the instrument is not neutral — it has a 
            position, a history, a set of commitments — and that those commitments have 
            aesthetic consequences.
          </RevealPara>
          <RevealPara delay={80}>
            The aesthetic is not decorative. It is argumentative. Every visual choice is a 
            claim about the nature of the examining function.
          </RevealPara>

          <div className="lore-type-specimens">
            <div className="lore-type-item">
              <div className="lore-type-sample lore-type-sample--serif">
                The bench receives all specimens without judgment.
              </div>
              <div className="lore-type-meta">
                <span className="lore-mono lore-mono--dim">Cormorant Garamond · Humanist Serif</span>
                <span className="lore-mono lore-mono--faint">The voice of the examination itself. Long-form, reflective, historically rooted.</span>
              </div>
            </div>
            <div className="lore-type-item">
              <div className="lore-type-sample lore-type-sample--display">
                SPECVLVM · Auditus
              </div>
              <div className="lore-type-meta">
                <span className="lore-mono lore-mono--dim">Cinzel · Classical Display</span>
                <span className="lore-mono lore-mono--faint">Titles, chapter markers, structural elements. The weight of institutional presence.</span>
              </div>
            </div>
            <div className="lore-type-item">
              <div className="lore-type-sample lore-type-sample--mono">
                §I · SIGN FUNCTION · ACTIVE · 0.00
              </div>
              <div className="lore-type-meta">
                <span className="lore-mono lore-mono--dim">IBM Plex Mono · Technical</span>
                <span className="lore-mono lore-mono--faint">Labels, codes, designations. The mechanism made visible.</span>
              </div>
            </div>
          </div>

          <div className="lore-subsection">
            <div className="lore-subsection-title">The Sigil Decoded</div>
            <div className="lore-sigil-decode">
              <div className="lore-sigil-decode-visual">
                <ChapterSigil size={180} animDelay={200} />
              </div>
              <div className="lore-sigil-decode-text">
                {[
                  { label: "Outer rings", desc: "The field of deployment — the space within which symbolic systems operate." },
                  { label: "Arc lines", desc: "The six principles of the Charter, intersecting at the center of every examination." },
                  { label: "Hexagram nodes", desc: "Six operators of meaning: sign, implication, myth, code, address, drift." },
                  { label: "Teardrop", desc: "The specimen. The text brought to the bench. The center that does not move while the apparatus revolves around it." },
                  { label: "Rotation", desc: "The examining function as dynamic process, not static judgment. The specimen does not change. The apparatus moves." },
                  { label: "Tick marks", desc: "Twenty-four intervals. The temporal dimension of all symbolic deployment." },
                ].map((item, i) => {
                  const [ref, visible] = useReveal(0.1);
                  return (
                    <div key={i} ref={ref}
                      className={`lore-decode-item ${visible ? "lore-decode-item--visible" : ""}`}
                      style={{ transitionDelay: `${i * 80}ms` }}>
                      <div className="lore-decode-label">{item.label}</div>
                      <div className="lore-decode-desc">{item.desc}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </LoreSection>

        {/* ══ §VIII — WHAT THIS IS NOT ══ */}
        <LoreSection id="lore-ch-8" num="VIII" title="A Note on What This Is Not"
          subtitle="The boundaries of the examining function are as important as its reach.">

          <div className="lore-not-grid">
            {[
              { label: "Not a fact-checker", desc: "SPECVLVM does not verify whether claims are true or false. Truth-value is not a semiotic question. The instrument examines how a text works, not whether its claims correspond to external reality." },
              { label: "Not a censor", desc: "The instrument does not determine whether a text should be deployed or suppressed. It has no prescriptive function. What the operator does with the findings is outside its scope — always." },
              { label: "Not a judge", desc: "It renders verdicts in the technical sense — structured assessments against the Charter's six criteria — but these verdicts carry no enforcement mechanism. They are findings, not rulings." },
              { label: "Not a substitute for human examination", desc: "The instrument's findings are a starting point for examination, not an endpoint. An operator who accepts the instrument's verdict without applying their own judgment has misused the tool." },
              { label: "Not neutral", desc: "The instrument has a position, a history, and a set of commitments. It was built within a tradition of applied semiotics that believes deployed language can and should be examined. That is a position. It is not hidden." },
              { label: "Not complete", desc: "Field Instrument №01 is the first in a suite of five. The full examining function requires all five. The Bench is the beginning, not the whole." },
            ].map((item, i) => {
              const [ref, visible] = useReveal(0.1);
              return (
                <div key={i} ref={ref}
                  className={`lore-not-card ${visible ? "lore-not-card--visible" : ""}`}
                  style={{ transitionDelay: `${i * 80}ms` }}>
                  <div className="lore-not-label">{item.label}</div>
                  <div className="lore-not-desc">{item.desc}</div>
                </div>
              );
            })}
          </div>

          <PullQuote
            text="The instrument examines. The operator decides. The Charter governs both."
          />
        </LoreSection>

        {/* ══ §IX — APPENDICES ══ */}
        <LoreSection id="lore-ch-9" num="IX" title="Appendices & Apparatus"
          subtitle="Supporting documents recovered from the Order's working papers. Select an entry to unseal it.">

          <RevealPara>
            What follows is not doctrine but apparatus — the working material behind the Charter.
            A lexicon for operators, the drift scale in full, the disputed text of the Publication
            Doctrine, and the marginalia the Compiler left in the only surviving draft.
          </RevealPara>

          <div className="appendix-list">
            <AppendixEntry index={0} code="APP · A" title="Lexicon of the Examining Function">
              <p className="appendix-para"><strong>Specimen</strong> — any text brought to the bench for examination. Advertisement, address, manifesto, packaging, post. The specimen is never altered, and never retained unless you save it to your archive (see APP · F).</p>
              <p className="appendix-para"><strong>Deployment</strong> — the act of releasing a symbolic system into a field of receivers. The Order examines deployment, not authorship; what a text does in the world, not what its author meant.</p>
              <p className="appendix-para"><strong>Apparatus</strong> — the total symbolic machinery of a specimen: its signs, codes, myths, and addressivity operating together.</p>
              <p className="appendix-para"><strong>Drift</strong> — the measured gap between stated purpose and actual mechanism. The operational signature of symbolic deception, sincere or otherwise.</p>
              <p className="appendix-para"><strong>The Acceleration</strong> — the collapsing interval between a message's deployment and its obsolescence, measured continuously since 1947.</p>
              <p className="appendix-para"><strong>The Bench</strong> — Field Instrument №01. The passive examining station. Where the specimen is brought, not sought.</p>
            </AppendixEntry>

            <AppendixEntry index={1} code="APP · B" title="The Drift Scale, In Full">
              <p className="appendix-para">Drift is reported as a float between 0.00 (perfect coherence between stated purpose and mechanism) and 1.00 (total divergence). The bands below are calibration guidance, not law.</p>
              <div className="appendix-scale">
                {[
                  { band: "0.00 – 0.20", name: "Coherent", note: "Stated purpose and mechanism agree. Rare. Usually plain, disclosed, low-intensity work." },
                  { band: "0.21 – 0.40", name: "Minor Drift", note: "Small undisclosed associations. Most honest commercial description lands here." },
                  { band: "0.41 – 0.65", name: "Operative Drift", note: "The mechanism is doing meaningfully more than the stated purpose admits. Examination warranted." },
                  { band: "0.66 – 0.85", name: "High Drift", note: "Stated purpose substantially conceals the actual operation. Most political and brand work." },
                  { band: "0.86 – 1.00", name: "Compromised", note: "Stated purpose and mechanism are effectively unrelated. The declaration is itself part of the apparatus." },
                ].map((b, i) => (
                  <div key={i} className="appendix-scale-row">
                    <span className="appendix-scale-band lore-mono lore-mono--gold">{b.band}</span>
                    <span className="appendix-scale-name">{b.name}</span>
                    <span className="appendix-scale-note">{b.note}</span>
                  </div>
                ))}
              </div>
            </AppendixEntry>

            <AppendixEntry index={2} code="APP · C" title="The Publication Doctrine (Disputed)">
              <p className="appendix-para">The text below is the 2019 proposal that fractured the Order. It is preserved here unedited, as the Charter requires — recorded, not resolved.</p>
              <p className="appendix-quote">"Where a specimen's Drift Quotient exceeds 0.80 and its field of deployment exceeds one million receivers, the examining function incurs a positive obligation: to release its findings into the public record, with the deployer named. To examine and withhold, under such conditions, is to become complicit in the drift one has measured. Silence is not neutrality. Silence is a second deployment."</p>
              <p className="appendix-para"><strong>The founding objection.</strong> To name the deployer is to render a verdict with force. The Order that publishes becomes the Order that judges, and the Order that judges accumulates power over the examined — the precise outcome the no-record commitment exists to prevent. The instrument that punishes is no longer an instrument. It is a court.</p>
              <p className="appendix-para">The debate was suspended, not settled. SPECVLVM's no-record clause is the suspension, written in code.</p>
            </AppendixEntry>

            <AppendixEntry index={3} code="APP · D" title="Marginalia — The Compiler's Draft">
              <p className="appendix-para">Six annotations survive in the margin of the only extant Charter draft. Authorship is attributed to the Compiler. They are reproduced without commentary.</p>
              <div className="appendix-margin">
                <p className="appendix-margin-line">— "Examination is not suspicion. The suspicious examiner finds only what they fear."</p>
                <p className="appendix-margin-line">— "A text may drift sincerely. Do not confuse drift with lying. Most drift is self-deception, deployed outward."</p>
                <p className="appendix-margin-line">— "The instrument that cannot examine itself will be examined by its absence."</p>
                <p className="appendix-margin-line">— "Measure the mechanism. The meaning belongs to the one who receives it."</p>
                <p className="appendix-margin-line">— "Six sections. Not because meaning has six parts, but because the hand has five fingers and one thumb to oppose them."</p>
                <p className="appendix-margin-line">— "When the bench can examine without me, I have not been replaced. I have been completed."</p>
              </div>
            </AppendixEntry>

            <AppendixEntry index={4} code="APP · E" title="Operator's Field Protocol">
              <p className="appendix-para">Recommended sequence for examining a specimen by hand, without the instrument — the discipline the bench automates.</p>
              <p className="appendix-para"><strong>1. Receive without judgment.</strong> Read the specimen once for what it says before asking what it does.</p>
              <p className="appendix-para"><strong>2. State the declared purpose.</strong> In one sentence, what does the text claim to be doing?</p>
              <p className="appendix-para"><strong>3. Run the six sections.</strong> Sign, denotation, myth, code, address, drift — in any order; they operate simultaneously.</p>
              <p className="appendix-para"><strong>4. Reconstruct the mechanism.</strong> In one sentence, what is the text actually doing?</p>
              <p className="appendix-para"><strong>5. Measure the gap.</strong> The distance between step 2 and step 4 is the drift.</p>
              <p className="appendix-para"><strong>6. Release the finding.</strong> The examination is complete. What you do next is not the instrument's concern.</p>
            </AppendixEntry>

            <AppendixEntry index={5} code="APP · F" title="Data & Privacy — What the Instrument Keeps">
              <p className="appendix-para">The Order's no-record commitment is operational, not absolute. In plain terms:</p>
              <p className="appendix-para"><strong>Unsaved audits.</strong> When you examine a specimen and do not save it, the text is sent to the examining apparatus, analysed, and returned to you. It is not written to any database. Closing the session ends its existence.</p>
              <p className="appendix-para"><strong>Saved examinations.</strong> If you choose to save an examination to your archive, its specimen text and findings are stored against your account so you can return to them. They remain until you delete them.</p>
              <p className="appendix-para"><strong>Account record.</strong> We hold your email, credit balance, examination count, and purchase history (provider, pack, amount, date) — the minimum required to run the account and the instrument.</p>
              <p className="appendix-para"><strong>Free-tier counting.</strong> For signed-out use, a count of free examinations is kept against your network address to enforce the limit. It carries no specimen text.</p>
              <p className="appendix-para"><strong>Processing.</strong> Specimen text is transmitted to the language model that powers the examination solely to produce your findings. Payment is handled by Stripe or PayPal; card details never touch the Order.</p>
              <p className="appendix-para"><strong>Your controls.</strong> Account → <em>Data &amp; Privacy</em> lets you export everything held about you as a file, or permanently erase your account, all saved examinations, and their text. Deletion is immediate and irreversible.</p>
              <p className="appendix-para" style={{color:"var(--ink-faint)"}}>Nothing is retained unless you save it. The findings are yours.</p>
            </AppendixEntry>
          </div>

          <PullQuote
            text="The apparatus is not secret. It was never secret. It was only unexamined."
          />
        </LoreSection>

        {/* ══ CLOSING ══ */}
        <div className="lore-closing">
          <div className="lore-closing-sigil">
            <ChapterSigil size={120} animDelay={200} />
          </div>
          <div className="lore-closing-rule" />
          <div className="lore-closing-text">
            <div className="lore-mono lore-mono--faint">ORDO · SEMEION · MMXXVI</div>
            <div className="lore-mono lore-mono--faint">INSTRUMENTA · AUDITUS · DISCIPLINA</div>
            <div className="lore-mono lore-mono--faint" style={{marginTop:12}}>
              FIELD INSTRUMENT №01 · REV. III · CHARTER §§I–VI
            </div>
          </div>
          <div className="lore-closing-rule" />
          <div className="lore-closing-motto">"No magic; only mechanism, made plain."</div>
          <div className="lore-closing-notice lore-mono lore-mono--faint">
            NO SPECIMENS STORED OUTSIDE THIS SESSION
          </div>
          <button className="lore-cta" onClick={() => go("intake")}>
            <span className="lore-mono">BEGIN EXAMINATION →</span>
          </button>
        </div>

      </div>
    </div>
  );
}

/* Expose entry component to global scope (per-file build) */
Object.assign(window, { Lore });
